import { useState, useEffect } from 'react';
import type {
  UseAutoSelectionParams,
  UseAutoSelectionReturn,
} from '../../../../types/whatsapp-address-selection.types';
import { getInitialAddressSelections } from '../../../../utils/whatsapp/address-selection.util';

/**
 * Selects the initial compatible pair once its inputs have loaded. Valid choices
 * remain stable during background refreshes, including deliberate user choices.
 * @param params - Available addresses, sessions and initial loading state.
 * @returns Selected identities and manual selection setters.
 */
export const useAutoSelection = ({
  whatsappContactAddresses,
  whatsappFunnelAddresses,
  channelSessions,
  isLoading = false,
}: UseAutoSelectionParams): UseAutoSelectionReturn => {
  const initialSelections = isLoading
    ? { contactId: null, funnelId: null }
    : getInitialAddressSelections(
        whatsappContactAddresses,
        whatsappFunnelAddresses,
        channelSessions
      );

  const [selectedToAddressId, setSelectedToAddressId] = useState<number | null>(
    initialSelections.contactId
  );
  const [selectedFromAddressId, setSelectedFromAddressId] = useState<
    number | null
  >(initialSelections.funnelId);

  const resolveAddressExists = <T extends { id: number }>(
    addresses: T[],
    selectedId: number | null
  ) => {
    if (selectedId === null) {
      return false;
    }

    return addresses.some((addr) => addr.id === selectedId);
  };

  useEffect(() => {
    // A partial availability result must not become a sticky default.
    if (isLoading) return;
    if (!whatsappContactAddresses.length || !whatsappFunnelAddresses.length) {
      setSelectedToAddressId(null);
      setSelectedFromAddressId(null);
      return;
    }

    const selectedToAddressStillExists = resolveAddressExists(
      whatsappContactAddresses,
      selectedToAddressId
    );
    const selectedFromAddressStillExists = resolveAddressExists(
      whatsappFunnelAddresses,
      selectedFromAddressId
    );

    if (selectedToAddressStillExists && selectedFromAddressStillExists) {
      return;
    }

    const fallbackSelections = getInitialAddressSelections(
      whatsappContactAddresses,
      whatsappFunnelAddresses,
      channelSessions
    );

    let newContactId: number | null = selectedToAddressStillExists
      ? selectedToAddressId
      : fallbackSelections.contactId;
    let newFunnelId: number | null = selectedFromAddressStillExists
      ? selectedFromAddressId
      : fallbackSelections.funnelId;

    const hasPreferredPair =
      fallbackSelections.contactId !== null &&
      fallbackSelections.funnelId !== null;
    const oneAddressMissing =
      (!selectedToAddressStillExists && selectedFromAddressStillExists) ||
      (selectedToAddressStillExists && !selectedFromAddressStillExists);

    if (hasPreferredPair && oneAddressMissing) {
      newContactId = fallbackSelections.contactId;
      newFunnelId = fallbackSelections.funnelId;
    }

    if (
      newContactId !== selectedToAddressId ||
      newFunnelId !== selectedFromAddressId
    ) {
      setSelectedToAddressId(newContactId);
      setSelectedFromAddressId(newFunnelId);
    }
  }, [
    whatsappContactAddresses,
    whatsappFunnelAddresses,
    channelSessions,
    isLoading,
    selectedToAddressId,
    selectedFromAddressId,
  ]);

  return {
    selectedToAddressId,
    selectedFromAddressId,
    setSelectedToAddressId,
    setSelectedFromAddressId,
  };
};
